import { create } from 'zustand';

import { TEAMSPACE_RELEASE_DATE } from '@/shared/constants/teamspace';
import { PROJECT_INSTANT_VOICE_PRESETS_RELEASE_DATE } from '@/shared/constants/voicePreset';
import { OnboardingStepKeys } from '@/shared/types/Onboarding';
import { User } from '@/shared/types/User';

type OnboardingState = {
  currentStep: null | OnboardingStepKeys;
  isInitialized: boolean;
  nextSteps: OnboardingStepKeys[];
  prevSteps: OnboardingStepKeys[];
  stepStatuses: Record<OnboardingStepKeys, boolean>;
};

type OnboardingActions = {
  changeStepStatus: (step: OnboardingStepKeys, status: boolean) => void;
  closeCurrentStep: () => void;
  restoreOptionalStep: (step: OnboardingStepKeys, beforeStep?: OnboardingStepKeys) => void;
  skipUnavailableStep: (step: OnboardingStepKeys) => void;
  setNextStep: (step?: OnboardingStepKeys) => void;
  updateOnboardingFromUser: (user: User, isBasicState: boolean) => void;
};

const initialState: OnboardingState = {
  currentStep: null,
  isInitialized: false,
  nextSteps: [],
  prevSteps: [],
  stepStatuses: {
    advancedTools_v1: true,
    audioTrackEditor_v1: true,
    changeTextSection_v1: true,
    clickToPlay_v1: true,
    customizeVoiceClone_v1: true,
    demoProjectWelcome_v1: false,
    discoverTeamspace_v1: true,
    downloadVideo_v1: false,
    editorActions_v1: true,
    manageTeamspace_v1: false,
    segmentPreview_v1: true,
    speakerVoicesButton_v1: true,
    transcriptVersions_v1: true,
    translateMyOwnVideoButton_v1: false,
    translationVariants_v1: true,
    videoPlayerTabs_v1: false,
  },
};

const basicInitialState: OnboardingState = {
  ...initialState,
  isInitialized: true,
  nextSteps: [
    'clickToPlay_v1',
    'changeTextSection_v1',
    'editorActions_v1',
    'audioTrackEditor_v1',
    'customizeVoiceClone_v1',
    'speakerVoicesButton_v1',
    'advancedTools_v1',
    'segmentPreview_v1',
    'transcriptVersions_v1',
    'translationVariants_v1',
    'downloadVideo_v1',
  ],
  stepStatuses: initialState.stepStatuses,
};

const trialDemoProjectsState: OnboardingState = {
  ...initialState,
  isInitialized: true,
  nextSteps: [
    'demoProjectWelcome_v1',
    'videoPlayerTabs_v1',
    'changeTextSection_v1',
    'editorActions_v1',
    'audioTrackEditor_v1',
    'customizeVoiceClone_v1',
    'advancedTools_v1',
    'segmentPreview_v1',
    'transcriptVersions_v1',
    'translationVariants_v1',
    'translateMyOwnVideoButton_v1',
  ],
  prevSteps: ['clickToPlay_v1', 'speakerVoicesButton_v1', 'downloadVideo_v1'],
  stepStatuses: {
    advancedTools_v1: true,
    audioTrackEditor_v1: true,
    changeTextSection_v1: true,
    clickToPlay_v1: false,
    customizeVoiceClone_v1: true,
    demoProjectWelcome_v1: true,
    discoverTeamspace_v1: false,
    downloadVideo_v1: false,
    editorActions_v1: true,
    manageTeamspace_v1: true,
    segmentPreview_v1: true,
    speakerVoicesButton_v1: false,
    transcriptVersions_v1: true,
    translateMyOwnVideoButton_v1: true,
    translationVariants_v1: true,
    videoPlayerTabs_v1: true,
  },
};

export const useOnboardingStore = create<OnboardingActions & OnboardingState>((set, get) => ({
  ...initialState,
  changeStepStatus: (step, status) =>
    set((state) => ({
      ...state,
      stepStatuses: {
        ...state.stepStatuses,
        [step]: status,
      },
    })),
  closeCurrentStep: () => {
    const { currentStep } = get();

    if (currentStep) {
      set((state) => ({
        ...state,
        currentStep: null,
        prevSteps: [...state.prevSteps, currentStep],
      }));
    }
  },
  restoreOptionalStep: (step, beforeStep) =>
    set((state) => {
      const isAlreadyKnown =
        !state.isInitialized ||
        state.currentStep === step ||
        state.prevSteps.includes(step) ||
        state.nextSteps.includes(step);

      if (isAlreadyKnown) {
        return state;
      }

      const nextSteps = [...state.nextSteps];
      const beforeStepIndex = beforeStep ? nextSteps.indexOf(beforeStep) : -1;

      if (beforeStepIndex === -1) {
        nextSteps.push(step);
      } else {
        nextSteps.splice(beforeStepIndex, 0, step);
      }

      return {
        ...state,
        nextSteps,
      };
    }),
  skipUnavailableStep: (step) => {
    const { currentStep, nextSteps } = get();
    const shouldAdvanceAfterSkip = currentStep === step || (!currentStep && nextSteps[0] === step);

    set((state) => ({
      ...state,
      currentStep: state.currentStep === step ? null : state.currentStep,
      nextSteps: state.nextSteps.filter((nextStep) => nextStep !== step),
    }));

    if (shouldAdvanceAfterSkip) {
      get().setNextStep();
    }
  },
  setNextStep: (step) => {
    const { nextSteps, stepStatuses } = get();

    if (step) {
      const stepIndex = nextSteps.indexOf(step);

      if (stepIndex !== -1) {
        set((state) => ({
          ...state,
          currentStep: step,
          nextSteps: state.nextSteps.filter((nextStep) => nextStep !== step),
        }));
      }
    }

    if (!step) {
      const [nextStep, ...steps] = nextSteps;

      if (nextStep && stepStatuses[nextStep]) {
        set((state) => ({
          ...state,
          currentStep: nextStep,
          nextSteps: [...steps],
        }));
      }
    }
  },
  updateOnboardingFromUser: (user, isBasicState: boolean) => {
    const isAlreadyInitialized = get().isInitialized;

    if (!isAlreadyInitialized) {
      const initState = isBasicState ? basicInitialState : trialDemoProjectsState;

      const completedSteps = user?.onboarding || [];
      const registeredBeforeCustomizeVoiceClone = user?.created_date
        ? new Date(user.created_date) < new Date(PROJECT_INSTANT_VOICE_PRESETS_RELEASE_DATE)
        : false;
      const registeredBeforeTeamspace = user?.created_date
        ? new Date(user.created_date) < new Date(TEAMSPACE_RELEASE_DATE)
        : false;
      const nextSteps = initState.nextSteps.filter((step) => !completedSteps.includes(step));

      set(() => ({
        ...initState,
        nextSteps,
        prevSteps: [...initState.prevSteps, ...completedSteps],
        stepStatuses: {
          ...initState.stepStatuses,
          customizeVoiceClone_v1: !registeredBeforeCustomizeVoiceClone,
          discoverTeamspace_v1: registeredBeforeTeamspace,
        },
      }));
    }
  },
}));
